import React, { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Routes, Route, useParams } from "react-router-dom";
import { LoginRequired } from "@authzsvc/common-react-components";
import { keycloakAuthenticated } from "../selectors";
import Dashboard from "./dashboard/Dashboard";
import MyGroupMemberships from "./group/MyGroupMemberships";
import GroupCreate from "./group/GroupCreate";
import GroupDetailsTabs from "./group/GroupDetailsTabs";
import GroupAdmin from "./group/GroupAdmin";
import MembershipSearch from "./group/MembershipSearch";
import RequestsWrapper from "./requests/RequestsWrapper";
import RequestDetailsWrapper from "./requests/RequestDetailsWrapper";
import GroupSubscriptionLink from "./group/GroupSubscriptionLink";
import GroupDetailsExternal from "./group/GroupDetailsExternal";
import { TitleContext } from "../hooks";
import { Layout } from "antd";
import { UsergroupAddOutlined, UserSwitchOutlined } from "@ant-design/icons";
import { userTokenHasRole } from "../selectors";

const MainRouter = () => {
  const authenticated = useSelector(keycloakAuthenticated);
  const { title } = useContext(TitleContext);

  useEffect(() => {
    document.title = title.toString().trim();
  }, [title]);

  if (!authenticated) return <LoginRequired />;

  return (
    <Layout.Content
      style={{ maxWidth: 1264, margin: "0 auto", padding: "32px" }}
    >
      <Routes>
        <Route path="/" element={<DashboardWrapper />} />
        <Route
          path="/group/:id/details"
          element={<GroupDetailsTabsWrapper />}
        />
        <Route
          path="/group/memberships/my"
          element={<MyMembershipsWrapper />}
        />
        <Route path="/group/create" element={<StaticGroupCreateWrapper />} />
        <Route
          path="/group/createDynamic"
          element={<DynamicGroupCreateWrapper />}
        />
        <Route
          path="/group/:id/subscribe"
          element={<GroupSubscriptionWrapper />}
        />
        <Route path="/groups/admin" element={<GroupAdminWrapper />} />
        <Route
          path="/groups/membership-search"
          element={<MembershipSearchWrapper />}
        />
        <Route path="/requests" element={<RequestsWrapperWrapper />} />
        <Route
          path="/requests/:id"
          element={<RequestDetailsWrapperWrapper />}
        />
        <Route path="/group/:id/:tab?" element={<GroupDetailsTabsWrapper />} />
      </Routes>
    </Layout.Content>
  );
};

// Wrapper components to call setCategory/setAction safely
const DashboardWrapper = () => {
  const { setCategory } = useContext(TitleContext);
  useEffect(() => setCategory("Search"), [setCategory]);
  return <Dashboard />;
};

const GroupSubscriptionWrapper = () => {
  const { setCategory } = useContext(TitleContext);
  useEffect(() => setCategory("Subscribe"), [setCategory]);
  return <GroupSubscriptionLink />;
};

const MyMembershipsWrapper = () => {
  const { setCategory } = useContext(TitleContext);
  useEffect(() => setCategory("My Memberships"), [setCategory]);
  return <MyGroupMemberships />;
};

const StaticGroupCreateWrapper = () => {
  const { setCategory, setAction } = useContext(TitleContext);
  useEffect(() => {
    setCategory("Create");
    setAction("Static group");
  }, [setCategory, setAction]);
  return (
    <GroupCreate
      dynamic={false}
      title="Create new static group"
      icon={<UsergroupAddOutlined />}
    />
  );
};

const DynamicGroupCreateWrapper = () => {
  const { setCategory, setAction } = useContext(TitleContext);
  useEffect(() => {
    setCategory("Create");
    setAction("Dynamic group");
  }, [setCategory, setAction]);
  return (
    <GroupCreate
      dynamic={true}
      title="Create new dynamic group"
      icon={<UserSwitchOutlined />}
    />
  );
};

const GroupAdminWrapper = () => {
  const { setCategory } = useContext(TitleContext);
  useEffect(() => setCategory("Admin"), [setCategory]);
  return <GroupAdmin />;
};

const MembershipSearchWrapper = () => {
  const { setCategory } = useContext(TitleContext);
  useEffect(() => setCategory("Membership Search"), [setCategory]);
  return <MembershipSearch />;
};

const RequestsWrapperWrapper = () => {
  const { setCategory } = useContext(TitleContext);
  useEffect(() => setCategory("Requests"), [setCategory]);
  return <RequestsWrapper />;
};

const GroupDetailsTabsWrapper = () => {
  const { setCategory } = useContext(TitleContext);
  const isExternal = useSelector(
    (state) => !userTokenHasRole(state, "group_reader")
  );
  useEffect(() => setCategory("Details"), [setCategory]);
  if (isExternal) {
    return <GroupDetailsExternal />;
  }
  return <GroupDetailsTabs />;
};

const RequestDetailsWrapperWrapper = () => {
  const { id } = useParams();
  const { setCategory, setAction } = useContext(TitleContext);

  useEffect(() => {
    setCategory("Requests");
    setAction(`ID ${id}`);
  }, [id, setCategory, setAction]);

  return <RequestDetailsWrapper />;
};

export default MainRouter;
